import { motion } from "framer-motion";
import { Flame, GraduationCap, Landmark, Medal, Stethoscope, Users } from "lucide-react";
import { Reveal } from "./Reveal";

const PHOTO_CARDS = [
  {
    slug: "educators",
    num: "01",
    title: "Educators",
    icon: GraduationCap,
    img: "/assets/p-educator.png",
  },
  {
    slug: "healthcare",
    num: "02",
    title: "Healthcare Professionals",
    icon: Stethoscope,
    img: "/assets/p-nurse.png",
  },
  {
    slug: "first-responders",
    num: "03",
    title: "First Responders",
    icon: Flame,
    img: "/assets/p-firefighter.png",
  },
  {
    slug: "military",
    num: "04",
    title: "Military Members",
    icon: Medal,
    img: "/assets/p-soldier.png",
  },
];

const TYPE_CARDS = [
  {
    slug: "government",
    num: "05",
    title: "Government Employees",
    icon: Landmark,
    img: "/assets/p-capitol.jpg",
    alt: "Georgia State Capitol building with its gold dome in Atlanta",
    tint: "from-[#060D1A] via-[#0B162C]/55 to-[#0B162C]/15",
    hoverTint: "group-hover:via-[#132342]/60",
  },
  {
    slug: "non-profit",
    num: "06",
    title: "Non-Profit Employees",
    icon: Users,
    img: "/assets/p-community.jpg",
    alt: "Neighbors gathered at a community street festival",
    tint: "from-[#450a0a] via-[#DC2626]/40 to-[#450a0a]/10",
    hoverTint: "group-hover:via-[#DC2626]/50",
  },
];

const grid = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export const Professions = () => (
  <section id="professions" data-testid="professions-section" className="bg-[#FAF8F5] pb-28 pt-8 sm:pb-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#D97706]">Chapter 02 — Who Qualifies?</p>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-[#0B162C] sm:text-3xl lg:text-4xl">
            Six kinds of heroes.
            <br />
            One promise.
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-slate-500 sm:text-base">
          If you serve your Georgia community in one of these roles, you may qualify for the 1% lender credit* on your next purchase
          or refinance.
        </p>
      </Reveal>

      <motion.div
        variants={grid}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="grid gap-5 md:grid-cols-2 lg:grid-cols-3"
      >
        {PHOTO_CARDS.map((c) => (
          <motion.article
            key={c.slug}
            variants={item}
            data-testid={`profession-card-${c.slug}`}
            className="group relative h-80 overflow-hidden rounded-2xl shadow-[0_16px_50px_rgba(11,22,44,0.18)]"
          >
            <img
              src={c.img}
              alt={c.title}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060D1A] via-[#060D1A]/35 to-[#060D1A]/5 transition-colors duration-500 group-hover:via-[#132342]/45" />
            <span className="absolute left-5 top-5 font-mono text-xs font-bold tracking-[0.2em] text-[#F59E0B]">{c.num}</span>
            <div className="absolute inset-x-0 bottom-0 p-6">
              <span className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#F59E0B]/20 text-[#F59E0B] ring-1 ring-[#F59E0B]/50 backdrop-blur-sm">
                <c.icon size={18} />
              </span>
              <h3 className="font-display text-xl font-bold text-[#FAF8F5]">{c.title}</h3>
            </div>
          </motion.article>
        ))}
        {TYPE_CARDS.map((c) => (
          <motion.article
            key={c.slug}
            variants={item}
            data-testid={`profession-card-${c.slug}`}
            className="group relative h-80 overflow-hidden rounded-2xl shadow-[0_16px_50px_rgba(11,22,44,0.18)]"
          >
            <img
              src={c.img}
              alt={c.alt}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div
              className={`absolute inset-0 bg-gradient-to-t ${c.tint} ${c.hoverTint} transition-colors duration-500`}
            />
            <span className="absolute left-5 top-5 font-mono text-xs font-bold tracking-[0.2em] text-[#F59E0B]">{c.num}</span>
            <div className="absolute inset-x-0 bottom-0 p-6">
              <span className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#F59E0B]/20 text-[#F59E0B] ring-1 ring-[#F59E0B]/50 backdrop-blur-sm">
                <c.icon size={18} />
              </span>
              <h3 className="font-display text-xl font-bold text-[#FAF8F5]">{c.title}</h3>
            </div>
          </motion.article>
        ))}
      </motion.div>
    </div>
  </section>
);
